import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Flame, ChevronRight, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { STATUS_META, ORDER } from "@/components/WhoIsInCard";

// Iter 145 — teams overview: every team with its headcount, who's in right
// now and who's on call. Built from the same /inout payload as the board.
export default function Teams() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/inout").then((r) => setData(r.data)).catch((e) => {
      if (e.response?.status === 403) navigate("/dashboard");
    });
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [navigate]);

  const groups = useMemo(() => {
    const staff = data?.staff || [];
    const leadOf = data?.team_leads || {};
    const teamsOf = (s) => (s.teams?.length ? s.teams : (s.team ? [s.team] : []));
    const map = new Map();
    for (const s of staff) {
      const list = teamsOf(s);
      for (const t of (list.length ? list : ["Unassigned"])) {
        if (!map.has(t)) map.set(t, []);
        map.get(t).push(s);
      }
    }
    return Array.from(map.entries())
      .map(([name, members]) => ({
        name,
        lead: members.find((m) => m.user_id === leadOf[name]) || null,
        members: members.sort((a, b) => (a.user_id === leadOf[name] ? -1 : b.user_id === leadOf[name] ? 1 : 0)
                                        || (a.name || "").localeCompare(b.name || "")),
        counts: ORDER.reduce((a, st) => { a[st] = members.filter((m) => m.status === st).length; return a; }, {}),
        onCall: members.filter((m) => m.on_call),
      }))
      .sort((a, b) => (a.name === "Unassigned" ? 1 : b.name === "Unassigned" ? -1 : b.members.length - a.members.length));
  }, [data]);

  if (user?.base_role !== "staff") return null;

  return (
    <div className="space-y-5" data-testid="teams-page">
      <div>
        <Link to="/in-out" className="mb-1 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary"
          data-testid="teams-back-link">
          <ArrowLeft className="h-3.5 w-3.5" /> In Out Board
        </Link>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Teams</h1>
        <p className="mt-1 text-sm text-slate-500">
          {groups.length} {groups.length === 1 ? "team" : "teams"} · headcount and who&apos;s in right now
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="teams-grid">
        {groups.map((g) => {
          const inNow = g.counts.in || 0;
          return (
            <div key={g.name} data-testid={`team-tile-${g.name}`}
              className="group flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {g.name === "Unassigned" ? (
                    <span className="text-base font-semibold text-foreground">Unassigned</span>
                  ) : (
                    <Link to={`/teams/${encodeURIComponent(g.name)}`} data-testid={`team-tile-link-${g.name}`}
                      className="text-base font-semibold text-foreground hover:text-primary hover:underline">{g.name}</Link>
                  )}
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users className="h-3.5 w-3.5" />
                    {g.members.length} {g.members.length === 1 ? "person" : "people"}
                    <span className="text-muted-foreground/50">·</span>
                    <span className={inNow ? "font-semibold text-emerald-600" : ""} data-testid={`team-tile-in-${g.name}`}>
                      {inNow} in now
                    </span>
                  </p>
                </div>
                {g.name !== "Unassigned" && (
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                )}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {ORDER.filter((s) => g.counts[s]).map((s) => (
                  <span key={s} data-testid={`team-tile-${g.name}-${s}`}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_META[s].chip}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[s].dot}`} />
                    {STATUS_META[s].label} <span className="font-bold">{g.counts[s]}</span>
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-1">
                {g.members.slice(0, 10).map((m) => (
                  <span key={m.user_id} title={`${m.name} · ${STATUS_META[m.status]?.label || m.status}`}
                    className={`rounded-full ring-2 ${m.status === "in" ? "ring-emerald-500" : "ring-transparent"}`}>
                    <Avatar name={m.name} src={m.avatar_url} size="sm" fallback="mystery" />
                  </span>
                ))}
                {g.members.length > 10 && (
                  <span className="ml-1 text-[11px] text-muted-foreground">+{g.members.length - 10}</span>
                )}
              </div>

              {g.lead && (
                <p className="text-[11px] text-muted-foreground" data-testid={`team-tile-lead-${g.name}`}>
                  Lead: <span className="font-semibold text-foreground">{g.lead.name}</span>
                </p>
              )}
              {g.onCall.length > 0 && (
                <p className="flex items-center gap-1.5 text-[11px] font-medium text-orange-600" data-testid={`team-tile-oncall-${g.name}`}>
                  <Flame className="h-3.5 w-3.5" fill="currentColor" />
                  On call: {g.onCall.map((m) => m.name).join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
